import Ticker from "../entities/Ticker";
import PackageTicker from "../entities/PackageTicker";
import Candle from "../entities/Candle";
import SimulationReport from "../entities/SimulationReport";
import SimulationReportChartData from "../entities/SimulationReportChartData";
import Model from "../entities/Model";
import ModelAnalysisResult from "../entities/ModelAnalysisResult";
import SignalFile from "../entities/SignalFile";
import ModelSpreadsheetCalculation from "../entities/ModelSpreadsheetCalculation";
import ModelExecutionResponse from "../entities/ModelExecutionResponse";
import Notification from "../entities/Notification";
import GeneratedPackage from "../entities/GeneratedPackage";
import TradingConnector from "../entities/TradingConnector";
import TradingStrategy from "../entities/TradingStrategy";

// Simple API client wrapper for backend requests with centralized verbs and endpoints.
// In development, use empty string to make relative URLs that go through the proxy
const defaultDevApi =
  process.env.NODE_ENV === "development" ? "" : "";
const apiBase =
  process.env.REACT_APP_API_BASE && process.env.REACT_APP_API_BASE.length > 0
    ? process.env.REACT_APP_API_BASE
    : defaultDevApi;

function getUrl(path) {
  if (!path) return apiBase;
  if (/^https?:\/\//i.test(path)) return path;
  const p = path.replace(/^\//, "");
  return apiBase ? `${apiBase}/${p}` : `/${p}`;
}

async function handleResponse(resp) {
  const contentType = resp.headers.get("content-type") || "";
  if (!resp.ok) {
    // try to return JSON error if possible
    if (contentType.indexOf("application/json") !== -1) {
      const json = await resp.json();
      const msg = json && json.message ? json.message : JSON.stringify(json);
      throw new Error(msg || resp.statusText);
    }
    const txt = await resp.text();
    throw new Error(txt || resp.statusText);
  }

  if (contentType.indexOf("application/json") !== -1) return resp.json();
  return resp.text();
}

// Centralized endpoints map (grouped). Add more endpoints here as needed.
export const endpoints = {
  tickers: {
    search: { method: "POST", url: "/api/tickers/search" },
    searchItems: { method: "POST", url: "/api/tickers/search/items-only" },
    update: { method: "POST", url: "/api/tickers" },
  },
  models: {
    search: { method: "POST", url: "/api/models/search" },
    save: { method: "POST", url: "/api/models" },
    get: { method: "GET", url: "/api/models" },
    delete: { method: "DELETE", url: "/api/models" },
    download: { method: "GET", url: "/api/models" },
  },
};

// low-level request helper that accepts either:
// - an endpoint key string like 'tickers.search'
// - a raw path '/api/...' or 'weatherforecast'
// - an endpoint descriptor object { method, url }
export async function request(endpointOrPath, body, options = {}) {
  let descriptor = null;

  if (typeof endpointOrPath === "string" && endpointOrPath.includes(".")) {
    const [group, name] = endpointOrPath.split(".");
    descriptor = (endpoints[group] && endpoints[group][name]) || null;
  }

  if (!descriptor) {
    if (typeof endpointOrPath === "string")
      descriptor = { url: endpointOrPath };
    else descriptor = endpointOrPath;
  }

  if (!descriptor || !descriptor.url)
    throw new Error("Invalid endpoint or path");

  const method = (
    options.method ||
    descriptor.method ||
    (body ? "POST" : "GET")
  ).toUpperCase();
  const url = getUrl(descriptor.url);

  const fetchOptions = {
    method,
    headers: { "Content-Type": "application/json" },
  };
  if (method !== "GET" && method !== "HEAD" && body !== undefined) {
    fetchOptions.body = JSON.stringify(body);
  }

  const resp = await fetch(url, fetchOptions);
  return handleResponse(resp);
}

export async function get(path, options = {}) {
  return request(path, undefined, { method: "GET", ...options });
}
export async function post(path, body, options = {}) {
  return request(path, body, { method: "POST", ...options });
}
export async function put(path, body, options = {}) {
  return request(path, body, { method: "PUT", ...options });
}
export async function patch(path, body, options = {}) {
  return request(path, body, { method: "PATCH", ...options });
}
export async function del(path, options = {}) {
  return request(path, undefined, { method: "DELETE", ...options });
}

export class ApiClient {
  constructor(baseUrl = '') {
    this.baseUrl = baseUrl.replace(/\/+$/, '');
  }

  async postJson(path, body, opts = {}) {
    const url = `${this.baseUrl}${path}`;
    const res = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(opts.headers || {}),
      },
      body: JSON.stringify(body),
      credentials: opts.credentials || 'same-origin',
    });
    if (!res.ok) {
      const text = await res.text().catch(() => '');
      let errorMessage = `Request failed: ${res.status} ${res.statusText}`;
      let structuredErrors = null;

      // Try to parse as JSON to get structured error response
      try {
        const errorData = JSON.parse(text);
        if (errorData && errorData.Errors && Array.isArray(errorData.Errors)) {
          structuredErrors = errorData.Errors;
          // Create a user-friendly error message from structured errors
          errorMessage = errorData.Errors.map(e => e.Message).join('; ');
        }
      } catch (parseError) {
        // If parsing fails, use the text as error message
        if (text) {
          errorMessage = text;
        }
      }

      const err = new Error(errorMessage);
      err.status = res.status;
      err.body = text;
      err.structuredErrors = structuredErrors;
      throw err;
    }
    return res;
  }

  async putJson(path, body, opts = {}) {
    const url = `${this.baseUrl}${path}`;
    const res = await fetch(url, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        ...(opts.headers || {}),
      },
      body: JSON.stringify(body),
      credentials: opts.credentials || 'same-origin',
    });
    if (!res.ok) {
      const text = await res.text().catch(() => '');
      let errorMessage = `Request failed: ${res.status} ${res.statusText}`;
      let structuredErrors = null;

      // Try to parse as JSON to get structured error response
      try {
        const errorData = JSON.parse(text);
        if (errorData && errorData.Errors && Array.isArray(errorData.Errors)) {
          structuredErrors = errorData.Errors;
          // Create a user-friendly error message from structured errors
          errorMessage = errorData.Errors.map(e => e.Message).join('; ');
        }
      } catch (parseError) {
        // If parsing fails, use the text as error message
        if (text) {
          errorMessage = text;
        }
      }

      const err = new Error(errorMessage);
      err.status = res.status;
      err.body = text;
      err.structuredErrors = structuredErrors;
      throw err;
    }
    return res;
  }

  async getJson(path, opts = {}) {
    const url = `${this.baseUrl}${path}`;
    const res = await fetch(url, {
      method: 'GET',
      headers: {
        ...(opts.headers || {}),
      },
      credentials: opts.credentials || 'same-origin',
    });
    if (!res.ok) {
      const text = await res.text().catch(() => '');
      let errorMessage = `Request failed: ${res.status} ${res.statusText}`;
      let structuredErrors = null;

      // Try to parse as JSON to get structured error response
      try {
        const errorData = JSON.parse(text);
        if (errorData && errorData.Errors && Array.isArray(errorData.Errors)) {
          structuredErrors = errorData.Errors;
          // Create a user-friendly error message from structured errors
          errorMessage = errorData.Errors.map(e => e.Message).join('; ');
        }
      } catch (parseError) {
        // If parsing fails, use the text as error message
        if (text) {
          errorMessage = text;
        }
      }

      const err = new Error(errorMessage);
      err.status = res.status;
      err.body = text;
      err.structuredErrors = structuredErrors;
      throw err;
    }
    return res;
  }

  async deleteJson(path, opts = {}) {
    const url = `${this.baseUrl}${path}`;
    const res = await fetch(url, {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
        ...(opts.headers || {}),
      },
      credentials: opts.credentials || 'same-origin',
    });
    if (!res.ok) {
      const text = await res.text().catch(() => '');
      let errorMessage = `Request failed: ${res.status} ${res.statusText}`;
      let structuredErrors = null;

      // Try to parse as JSON to get structured error response
      try {
        const errorData = JSON.parse(text);
        if (errorData && errorData.Errors && Array.isArray(errorData.Errors)) {
          structuredErrors = errorData.Errors;
          // Create a user-friendly error message from structured errors
          errorMessage = errorData.Errors.map(e => e.Message).join('; ');
        }
      } catch (parseError) {
        // If parsing fails, use the text as error message
        if (text) {
          errorMessage = text;
        }
      }

      const err = new Error(errorMessage);
      err.status = res.status;
      err.body = text;
      err.structuredErrors = structuredErrors;
      throw err;
    }
    return res;
  }

  async searchTickers(body) {
    const res = await this.postJson('/api/tickers/search', body);
    const data = await res.json();
    data.Items = data.Items.map((d) => new Ticker(d));
    return data;
  }

  async searchTickerItems(body) {
    const res = await this.postJson('/api/tickers/search/items-only', body);
    const data = await res.json();
    return data.map((d) => new Ticker(d));
  }

  async getPackageTickers() {
    const res = await this.getJson('/api/tickers/package-tickers-status');
    const data = await res.json();
    return (data || []).map((d) => {
      const t = new PackageTicker(d);
      t.LatestCandle = d.LatestCandle ? new Candle(d.LatestCandle) : null;
      return t;
    });
  }

  // ----- Package Tickers (admin CRUD) -----

  async listPackageTickers() {
    const res = await this.getJson('/api/package-tickers');
    if (!res.ok) {
      const text = await res.text().catch(() => '');
      throw new Error(text || `Request failed: ${res.status}`);
    }
    return await res.json();
  }

  async savePackageTicker(body) {
    const res = await this.postJson('/api/package-tickers', body);
    return await res.json();
  }

  async deletePackageTicker(id) {
    const res = await this.deleteJson(`/api/package-tickers/${id}`);
    if (!res.ok) {
      const text = await res.text().catch(() => '');
      throw new Error(text || `Request failed: ${res.status}`);
    }
    return true;
  }

  // ----- Package Tickers Dates Validation -----
  //
  // The user picks a single primary PackageTicker and one or more extras
  // on the admin "Package Tickers Dates Validation" page; the server reads
  // the corresponding CSVs from the Analysis folder (the same one used by
  // the Models Analysis page) and reports the date-set diff per extra.
  async analyzePackageTickersDatesValidation(body) {
    const res = await this.postJson(
      '/api/package-tickers-dates-validation/analyze',
      body || {});
    if (!res.ok) {
      const text = await res.text().catch(() => '');
      throw new Error(text || `Request failed: ${res.status}`);
    }
    return await res.json();
  }

  // ----- Package Tickers Validation (S&P 500 1950 baseline) -----
  //
  // The user clicks a single "Generate report" button on the admin
  // "Package tickers validation" page; the server uses the
  // "S&P 500 1950" PackageTicker row's most-recent day candle as the
  // baseline and walks every other package ticker, reporting which have
  // that date and which do not. The result is stored as a PDF row that
  // can be downloaded later.
  async generateTickersReport(body) {
    const res = await this.postJson('/api/tickers-reports/generate', body || {});
    if (!res.ok) {
      const text = await res.text().catch(() => '');
      throw new Error(text || `Request failed: ${res.status}`);
    }
    return await res.json();
  }

  async listTickersReports(params = {}) {
    const qs = new URLSearchParams();
    if (params.referencePackageTickerId) {
      qs.set('referencePackageTickerId', params.referencePackageTickerId.toString());
    }
    if (params.offset !== undefined && params.offset !== null) {
      qs.set('offset', params.offset.toString());
    }
    if (params.limit !== undefined && params.limit !== null) {
      qs.set('limit', params.limit.toString());
    }
    const query = qs.toString();
    const path = query ? `/api/tickers-reports?${query}` : '/api/tickers-reports';
    const res = await this.getJson(path);
    if (!res.ok) {
      const text = await res.text().catch(() => '');
      throw new Error(text || `Request failed: ${res.status}`);
    }
    return await res.json();
  }

  // Triggers a browser download for the given report. Uses the cookie
  // session for auth (same-origin) and lets the browser pick up the
  // Content-Disposition filename from the server.
  downloadTickersReport(id) {
    const url = `${this.baseUrl}/api/tickers-reports/${encodeURIComponent(id)}/download`;
    window.location.href = url;
  }

  // Lookup helpers for small reference lists (sectors, industries, countries, exchanges)
  // These endpoints exist on the server and return arrays of { Id, Name }.
  async searchSectorsItems(body) {
    const res = await this.postJson('/api/sectors/search/items-only', body || {});
    const data = await res.json();
    return data;
  }

  async searchIndustriesItems(body) {
    const res = await this.postJson('/api/industries/search/items-only', body || {});
    const data = await res.json();
    return data;
  }

  async searchCountriesItems(body) {
    const res = await this.postJson('/api/countries/search/items-only', body || {});
    const data = await res.json();
    return data;
  }

  async searchExchangesItems(body) {
    const res = await this.postJson('/api/exchanges/search/items-only', body || {});
    const data = await res.json();
    return data;
  }

  // Cached maps (Id -> Name)
  async getSectorsMap() {
    if (this._sectorsMap) return this._sectorsMap;
    try {
      const items = await this.searchSectorsItems({ Limit: 10000, Offset: 0 });
      const map = {};
      (items || []).forEach((s) => {
        if (s && s.Id != null) map[s.Id] = s.Name;
      });
      this._sectorsMap = map;
      return map;
    } catch (err) {
      console.error('Failed to load sectors', err);
      return {};
    }
  }

  async getIndustriesMap() {
    if (this._industriesMap) return this._industriesMap;
    try {
      const items = await this.searchIndustriesItems({ Limit: 10000, Offset: 0 });
      const map = {};
      (items || []).forEach((s) => {
        if (s && s.Id != null) map[s.Id] = s.Name;
      });
      this._industriesMap = map;
      return map;
    } catch (err) {
      console.error('Failed to load industries', err);
      return {};
    }
  }

  async getCountriesMap() {
    if (this._countriesMap) return this._countriesMap;
    try {
      const items = await this.searchCountriesItems({ Limit: 10000, Offset: 0 });
      const map = {};
      (items || []).forEach((s) => {
        if (s && s.Id != null) map[s.Id] = s.Name;
      });
      this._countriesMap = map;
      return map;
    } catch (err) {
      console.error('Failed to load countries', err);
      return {};
    }
  }

  async getExchangesMap() {
    if (this._exchangesMap) return this._exchangesMap;
    try {
      const items = await this.searchExchangesItems({ Limit: 10000, Offset: 0 });
      const map = {};
      (items || []).forEach((s) => {
        if (s && s.Id != null) map[s.Id] = s.Name;
      });
      this._exchangesMap = map;
      return map;
    } catch (err) {
      console.error('Failed to load exchanges', err);
      return {};
    }
  }

  async updateTicker(body) {
    const res = await this.postJson('/api/tickers', body);
    return res.json();
  }

  async updateTickerCountry(tickerId, body) {
    const res = await this.postJson(`/api/tickers/${tickerId}/country`, body || {});
    return res.json();
  }

  async fetchTickerCandles(tickerId, interval, body) {
    const path = `/api/tickers/${tickerId}/candles/${interval}`;
    const res = await this.postJson(path, body || {});
    const data = await res.json();
    return data.map((d) => new Candle(d));
  }

  async searchCandles(body) {
    const res = await this.postJson('/api/candles/search', body);
    const data = await res.json();
    data.Items = data.Items.map((d) => new Candle(d));
    return data;
  }

  async searchSimulationReports(body) {
    const res = await this.postJson('/api/dmsimulationreports/search', body);
    const data = await res.json();
    data.Items = data.Items.map((d) => new SimulationReport(d));
    return data;
  }

  async deleteSimulationReport(id) {
    const res = await this.deleteJson(`/api/dmsimulationreports/${id}`);
    if (!res.ok) {
      const text = await res.text().catch(() => '');
      throw new Error(text || `Request failed: ${res.status}`);
    }
    return true;
  }

  async getSimulationReportChartData(id) {
    const res = await this.getJson(`/api/dmsimulationreports/${id}/chart-data`);
    const data = await res.json();
    return new SimulationReportChartData(data);
  }

  async getSimulationReportTrades(id, offset = 0, limit = 25) {
    const res = await this.getJson(`/api/dmsimulationreports/${id}/trades?offset=${offset}&limit=${limit}`);
    const data = await res.json();
    return data;
  }

  async getSimulationReportNotifications(id, offset = 0, limit = 25) {
    const res = await this.getJson(`/api/dmsimulationreports/${id}/notifications?offset=${offset}&limit=${limit}`);
    const data = await res.json();
    return data;
  }

  async getDecisionMakers() {
    const res = await this.getJson('/api/decisionmakers');
    return await res.json();
  }

  async triggerSimulation(body) {
    const res = await this.postJson('/api/decisionmakers/simulate/batch', body);
    // This endpoint returns void on success
    return res.ok;
  }

  async simulateDecisionMaker(body) {
    const res = await this.postJson('/api/decisionmakers/simulate', body);
    const data = await res.json();
    return new SimulationReportChartData(data);
  }

  // Generic package fetch that returns an object URL + filename + blob
  async fetchPackage(body, packageType) {
    const path = `/api/packages/${packageType}`;
    const url = `${this.baseUrl}${path}`;
    const res = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
      credentials: 'same-origin',
    });

    if (!res.ok) {
      const txt = await res.text().catch(() => '');
      throw new Error(`Server error: ${res.status} ${txt}`);
    }

    const blob = await res.blob();
    const cd = res.headers.get('content-disposition') || '';
    let filename = 'package.zip';
    const m = /filename\*=UTF-8''([^;]+)|filename="([^"]+)"|filename=([^;]+)/i.exec(cd);
    if (m) {
      const raw = decodeURIComponent((m[1] || m[2] || m[3] || '').replace(/^['"]|['"]$/g, ''));
      if (raw) filename = raw;
    }

    const objUrl = window.URL.createObjectURL(blob);
    return { url: objUrl, filename };
  }

  // Model API methods
  async searchModels(body) {
    const res = await this.postJson('/api/models/search', body);
    const data = await res.json();
    data.Items = data.Items.map((d) => new Model(d));
    return data;
  }

  async getModel(id) {
    const res = await this.getJson(`/api/models/${id}`);
    return await res.json();
  }

  async saveModel(body) {
    const res = await this.postJson('/api/models', body);
    const data = await res.json();
    return new Model(data);
  }

  async deleteModel(id) {
    const res = await fetch(`${this.baseUrl}/api/models/${id}`, {
      method: 'DELETE',
      credentials: 'same-origin',
    });
    if (!res.ok) {
      const text = await res.text().catch(() => '');
      const err = new Error(`Request failed: ${res.status} ${res.statusText}`);
      err.status = res.status;
      err.body = text;
      throw err;
    }
    return res.ok;
  }

  async downloadModel(id) {
    const url = `${this.baseUrl}/api/models/${id}/download`;
    const res = await fetch(url, { method: 'GET', credentials: 'same-origin' });

    if (!res.ok) {
      const txt = await res.text().catch(() => '');
      throw new Error(`Server error: ${res.status} ${txt}`);
    }

    const blob = await res.blob();
    const cd = res.headers.get('content-disposition') || '';

    // Parse only filename*=UTF-8'' — this is the RFC 5987 encoded value and is
    // always correct. The plain filename= token may be sanitized by the server.
    let filename = 'model_file';
    const starMatch = /filename\*=UTF-8''([^;\s]+)/i.exec(cd);
    if (starMatch) {
      // Decode only valid %XX sequences; leave any bare % intact
      filename = starMatch[1].replace(/%([0-9A-Fa-f]{2})/g, (_, hex) => String.fromCharCode(parseInt(hex, 16)));
    }

    return { blob, filename };
  }

  async exportModelsZip(ids) {
    const res = await this.postJson('/api/models/export-zip', { Ids: ids });

    if (!res.ok) {
      const txt = await res.text().catch(() => '');
      throw new Error(`Server error: ${res.status} ${txt}`);
    }

    const blob = await res.blob();
    const cd = res.headers.get('content-disposition') || '';
    let filename = 'models_export.zip';
    const starMatch = /filename\*=UTF-8''([^;\s]+)/i.exec(cd);
    if (starMatch) {
      filename = starMatch[1].replace(/%([0-9A-Fa-f]{2})/g, (_, hex) => String.fromCharCode(parseInt(hex, 16)));
    }
    return { blob, filename };
  }

  async importModelsZip(zipBlob, fileName) {
    // Convert the zip blob to a hex string so it rides the same wire
    // format as the single-model FileData field. The server expects a
    // zip-shaped byte sequence and BytesToHexConverter decodes it
    // back into a MemoryStream on the C# side.
    const arrayBuffer = await zipBlob.arrayBuffer();
    const bytes = new Uint8Array(arrayBuffer);
    const hex = Array.from(bytes, b => b.toString(16).padStart(2, '0')).join('');

    const res = await fetch(`${this.baseUrl}/api/models/import-zip`, {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        FileName: fileName || 'models.zip',
        ZipData: hex,
      }),
    });

    if (!res.ok) {
      const txt = await res.text().catch(() => '');
      throw new Error(txt || `Server error: ${res.status} ${res.statusText}`);
    }

    return await res.json();
  }

  async getModelSubscription(id) {
    const res = await this.getJson(`/api/models/${id}/subscription`);
    return await res.json();
  }

  // -----------------------------------------------------------------
  // Visible-to-users management
  //
  // Each Model row carries an array of user ids that have been granted
  // visibility on a non-public model. The endpoints below mutate that
  // array — the controller wraps every mutating call in a transaction
  // with a SELECT ... FOR UPDATE row lock so concurrent admin edits
  // can't lose each other's appends.
  // -----------------------------------------------------------------

  async addVisibleUser(modelId, userId) {
    const res = await fetch(`${this.baseUrl}/api/models/${modelId}/visible-users`, {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ UserId: userId }),
    });
    if (!res.ok) {
      const text = await res.text().catch(() => '');
      throw new Error(text || `Request failed: ${res.status} ${res.statusText}`);
    }
    return await res.json();
  }

  async removeVisibleUser(modelId, userId) {
    const res = await fetch(`${this.baseUrl}/api/models/${modelId}/visible-users/${userId}`, {
      method: 'DELETE',
      credentials: 'same-origin',
    });
    if (!res.ok) {
      const text = await res.text().catch(() => '');
      throw new Error(text || `Request failed: ${res.status} ${res.statusText}`);
    }
    return await res.json();
  }

  async isUserVisibleToModel(modelId, userId) {
    const res = await this.getJson(`/api/models/${modelId}/visible-users/${userId}`);
    return await res.json();
  }

  async subscribeModel(id) {
    const res = await fetch(`${this.baseUrl}/api/models/${id}/subscription`, {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json' },
    });
    if (!res.ok) {
      const text = await res.text().catch(() => '');
      const err = new Error(`Request failed: ${res.status} ${res.statusText}`);
      err.status = res.status;
      err.body = text;
      throw err;
    }
  }

  async unsubscribeModel(id) {
    const res = await fetch(`${this.baseUrl}/api/models/${id}/subscription`, {
      method: 'DELETE',
      credentials: 'same-origin',
    });
    if (!res.ok) {
      const text = await res.text().catch(() => '');
      const err = new Error(`Request failed: ${res.status} ${res.statusText}`);
      err.status = res.status;
      err.body = text;
      throw err;
    }
  }

  async getModelsList() {
    const res = await this.getJson('/api/models/list');
    const data = await res.json();
    return data.map((d) => new Model(d));
  }

  async executeModel(modelId, useOriginalCtracPackage = false, useRealTimeCandles = false) {
    const res = await this.postJson('/api/models/execute', {
      ModelId: modelId,
      UseOriginalCtracPackage: useOriginalCtracPackage,
      UseRealTimeCandles: useRealTimeCandles
    });
    const data = await res.json();
    return new ModelExecutionResponse(data);
  }

  async getModelSpreadsheet(modelId, options = {}) {
    const body = {
      ModelId: modelId,
    };
    if (options.startRow != null) body.StartRow = options.startRow;
    if (options.endRow != null) body.EndRow = options.endRow;
    const res = await this.postJson('/api/spreadsheets', body);
    return await res.json();
  }

  async getRecentModelSpreadsheets(modelId, limit = 5, options = {}) {
    const qs = new URLSearchParams({ limit: limit.toString() });
    if (options.includeEdits) qs.set('includeEdits', 'true');
    const res = await this.getJson(
      `/api/spreadsheets/${modelId}/recent?${qs.toString()}`
    );
    return await res.json();
  }

  // Look up the per-user edited sibling for the supplied original
  // calculation. Returns null when no edited sibling exists yet.
  async getEditedModelSpreadsheet(originalSpreadsheetCalculationId) {
    try {
      const res = await this.getJson(
        `/api/spreadsheets/${encodeURIComponent(originalSpreadsheetCalculationId)}/edited`
      );
      const data = await res.json();
      return new ModelSpreadsheetCalculation(data);
    } catch (err) {
      if (err && err.status === 404) return null;
      throw err;
    }
  }

  async getModelSpreadsheetRows(calculationDbId, options = {}) {
    const body = { CalculationDbId: calculationDbId };
    if (options.startRow != null) body.StartRow = options.startRow;
    if (options.limit != null) body.Limit = options.limit;
    const res = await this.postJson('/api/spreadsheets/rows', body);
    return await res.json();
  }

  async getModelSpreadsheetRowsFiltered(calculationDbId, shareId, options = {}) {
    const body = {
      CalculationDbId: calculationDbId,
      ShareId: shareId,
    };
    if (options.startRow != null) body.StartRow = options.startRow;
    if (options.limit != null) body.Limit = options.limit;
    const res = await this.postJson('/api/spreadsheets/rows/filtered', body);
    return await res.json();
  }

  // ----- Save spreadsheet edits (per-user bulk endpoint) -----
  // Edits are buffered client-side in the spreadsheet component (each
  // cell highlighted in orange) and only committed to the server when
  // the user clicks Save. The full pending dictionary goes up in a
  // single POST; the backend runs the engine once with the merged
  // dictionary, persists a per-user edited sibling row, and returns
  // the slim acknowledgement.
  async saveSpreadsheetEdits(modelId, originalSpreadsheetCalculationId, edits, lastEdit) {
    const body = {
      OriginalSpreadsheetCalculationId: originalSpreadsheetCalculationId,
      Edits: edits,
      LastEditColumnIndex: lastEdit.columnIndex,
      LastEditRowIndex: lastEdit.rowIndex,
    };
    const res = await this.postJson(
      `/api/spreadsheets/${encodeURIComponent(modelId)}/save-edits`,
      body
    );
    return await res.json();
  }

  // ----- Compare spreadsheets (admin) -----

  async compareModelSpreadsheets(modelId, options = {}) {
    const body = { ModelId: modelId };
    if (options.existingDbCalculationDbId != null) {
      body.ExistingDbCalculationDbId = options.existingDbCalculationDbId;
    }
    if (options.existingOriginalCalculationDbId != null) {
      body.ExistingOriginalCalculationDbId = options.existingOriginalCalculationDbId;
    }
    if (options.invalidateCache != null) {
      body.InvalidateCache = options.invalidateCache;
    }
    const res = await this.postJson('/api/spreadsheets/compare', body);
    return await res.json();
  }

  async getRecentModelSpreadsheetsBySource(modelId, dataSource, limit = 5) {
    const qs = new URLSearchParams({
      dataSource,
      limit: limit.toString(),
    });
    const res = await this.getJson(
      `/api/spreadsheets/${modelId}/recent-by-source?${qs.toString()}`
    );
    const data = await res.json();
    return {
      ...data,
      Items: (data.Items || []).map((d) => new ModelSpreadsheetCalculation(d)),
    };
  }

  // Fetch one page of the admin "Compare Spreadsheets" cached CSV for the
  // given model, mode ("consolidated" | "differences") and comparisonMode
  // ("display" | "raw"). The CSV itself is produced server-side by
  // /api/spreadsheets/compare and lives at
  // <LocalStorage>/Comparison/Model-{id}-{mode}-{display|raw}.csv.
  async getModelComparisonRows(modelId, mode, options = {}) {
    const body = { ModelId: modelId, Mode: mode };
    if (options.comparisonMode) body.ComparisonMode = options.comparisonMode;
    if (options.startRow != null) body.StartRow = options.startRow;
    if (options.limit != null) body.Limit = options.limit;
    const res = await this.postJson('/api/spreadsheets/compare/rows', body);
    return await res.json();
  }

  // Delete a persisted spreadsheet calculation (header + all cell rows).
  // Server returns 204 No Content on success and 404 when no calculation
  // exists with the supplied id; throws with structuredErrors on any other
  // non-2xx status (see `deleteJson`).
  async deleteModelSpreadsheet(calculationDbId) {
    if (calculationDbId == null || calculationDbId <= 0) {
      throw new Error("calculationDbId is required");
    }
    return await this.deleteJson(`/api/spreadsheets/${calculationDbId}`);
  }

  // Fetch the structured log of warnings produced by a persisted spreadsheet
  // run (today this is the log of rows removed by date alignment, one
  // record per removed row). Returned shape is
  // `{ CalculationDbId, Warnings: ModelSpreadsheetWarning[] }` where each
  // warning is `{ Type, Date, DataSource: string[] }`. Fetched lazily on
  // demand (typically when the user clicks the warning icon on a calculation
  // that has at least one warning) so the per-page rows payload does not
  // have to carry this list every time the user paginates.
  async getModelSpreadsheetWarnings(calculationDbId) {
    if (calculationDbId == null || calculationDbId <= 0) {
      throw new Error("calculationDbId is required");
    }
    const res = await this.getJson(
      `/api/spreadsheets/${calculationDbId}/warnings`
    );
    return await res.json();
  }

  // ----- Model shares -----

  async listModelShares(modelId) {
    const qs = modelId ? `?modelId=${encodeURIComponent(modelId)}` : "";
    const res = await this.getJson(`/api/model-shares${qs}`);
    return await res.json();
  }

  async listSharesSharedWithMe() {
    const res = await this.getJson('/api/model-shares/shared-with-me');
    return await res.json();
  }

  async getModelShare(id) {
    const res = await this.getJson(`/api/model-shares/${id}`);
    return await res.json();
  }

  async createModelShare(body) {
    const res = await this.postJson('/api/model-shares', body);
    return await res.json();
  }

  async updateModelShare(id, body) {
    const res = await this.putJson(`/api/model-shares/${id}`, body);
    return await res.json();
  }

  async deleteModelShare(id) {
    const res = await this.deleteJson(`/api/model-shares/${id}`);
    return res.ok;
  }

  async compareModelSignals(modelId) {
    const res = await this.postJson('/api/models/compare-signals', {
      ModelId: modelId
    });
    return await res.json();
  }

  async compareModelSignalsVsInternal(modelId) {
    const res = await this.postJson('/api/models/compare-signals-internal', {
      ModelId: modelId
    });
    return await res.json();
  }

  async getRecentSignals(body) {
    const res = await this.postJson('/api/models/recentsignals', body);
    return await res.json();
  }

  async getSignalOverlay(modelId) {
    const res = await this.getJson(`/api/models/${modelId}/signal-overlay`);
    return await res.json();
  }

  async getSignalFile(id) {
    const res = await this.getJson(`/api/signal-files/${id}`);
    return await res.json();
  }

  async combineSignalFiles(signalFileIds, combineMode) {
    const res = await this.postJson('/api/signal-files/combine', {
      SignalFileIds: signalFileIds,
      CombineMode: combineMode,
    });
    return await res.json();
  }

  async downloadCombinedSignalFile(signalFileIds, combineMode) {
    const res = await this.postJson('/api/signal-files/combine/download', {
      SignalFileIds: signalFileIds,
      CombineMode: combineMode,
    });
    if (!res.ok) {
      const txt = await res.text().catch(() => '');
      throw new Error(`Server error: ${res.status} ${txt}`);
    }
    const blob = await res.blob();
    const cd = res.headers.get('content-disposition') || '';
    let filename = 'combined_signal.csv';
    const m = /filename\*=UTF-8''([^;]+)|filename="([^"]+)"|filename=([^;]+)/i.exec(cd);
    if (m) {
      const raw = decodeURIComponent((m[1] || m[2] || m[3] || '').replace(/^['"]/,'').replace(/['"]$/,''));
      if (raw) filename = raw;
    }
    const url = window.URL.createObjectURL(blob);
    return { url, filename };
  }

  async analyzeModels(tolerance = 0.005) {
    const params = new URLSearchParams();
    if (tolerance !== null && tolerance !== undefined && !Number.isNaN(tolerance)) {
      params.set('tolerance', tolerance.toString());
    }
    const qs = params.toString();
    const url = qs ? `/api/models/analyze?${qs}` : '/api/models/analyze';
    const res = await this.getJson(url);
    const data = await res.json();
    return {
      Models: (data.Models || []).map((d) => new ModelAnalysisResult(d)),
      UniqueDataSources: data.UniqueDataSources || []
    };
  }

  async getCandleDifferences(subfolder, tickerCode, tickerId, page = 1, pageSize = 50, tolerance = 0.005) {
    const params = new URLSearchParams({
      subfolder,
      tickerCode,
      tickerId: tickerId.toString(),
      page: page.toString(),
      pageSize: pageSize.toString()
    });
    if (tolerance !== null && tolerance !== undefined && !Number.isNaN(tolerance)) {
      params.set('tolerance', tolerance.toString());
    }
    const res = await this.getJson(`/api/models/candle-differences?${params}`);
    const data = await res.json();
    return data;
  }

  async getCustomTableDifferences(subfolder, tickerCode, tickerName, page = 1, pageSize = 50, tolerance = 0.005) {
    const params = new URLSearchParams({
      subfolder,
      tickerCode,
      tickerName: tickerName || '',
      page: page.toString(),
      pageSize: pageSize.toString()
    });
    if (tolerance !== null && tolerance !== undefined && !Number.isNaN(tolerance)) {
      params.set('tolerance', tolerance.toString());
    }
    const res = await this.getJson(`/api/models/custom-table-differences?${params}`);
    const data = await res.json();
    return data;
  }

  async getCandleExtraDbDates(subfolder, tickerCode, tickerId, page = 1, pageSize = 50) {
    const params = new URLSearchParams({
      subfolder,
      tickerCode,
      tickerId: tickerId.toString(),
      page: page.toString(),
      pageSize: pageSize.toString()
    });
    const res = await this.getJson(`/api/models/candle-extra-db-dates?${params}`);
    return await res.json();
  }

  async deleteCandleByDate(tickerId, date, interval = 'Day') {
    const res = await this.postJson('/api/candles/delete-by-date', {
      tickerId,
      date,
      interval
    });
    return await res.json();
  }

  async getAnalysisPackageStatus() {
    const res = await this.getJson('/api/models/analysis-package/status');
    return await res.json();
  }

  async analyzePackageTicker(packageTickerId, tolerance = 0.005) {
    const params = new URLSearchParams();
    if (tolerance !== null && tolerance !== undefined && !Number.isNaN(tolerance)) {
      params.set('tolerance', tolerance.toString());
    }
    const qs = params.toString();
    const url = qs
      ? `/api/models/package-tickers/${packageTickerId}/analyze?${qs}`
      : `/api/models/package-tickers/${packageTickerId}/analyze`;
    const res = await this.getJson(url);
    return await res.json();
  }

  async regenerateAnalysisPackage() {
    const res = await this.postJson('/api/models/analysis-package/regenerate', {});
    return await res.json();
  }

  async searchSignalFiles(body) {
    const res = await this.postJson('/api/signal-files/search', body);
    const data = await res.json();
    data.Items = data.Items.map((d) => new SignalFile(d));
    return data;
  }

  async searchSignalFilesByCalculationId(body) {
    const res = await this.postJson('/api/signal-files/by-calculation-id', body);
    const data = await res.json();
    // Each row carries a CreateTime — wrap in a SignalFile so callers
    // can call `getFormattedCreateTime()` directly without re-running
    // moment formatting at the UI layer.
    if (data && Array.isArray(data.Items)) {
      data.Items = data.Items.map((d) => new SignalFile(d));
    }
    return data;
  }

  async deleteSignalFilesByCalculationId(calculationId) {
    const res = await this.deleteJson(`/api/signal-files/by-calculation-id/${encodeURIComponent(calculationId)}`);
    return await res.json();
  }

  async getSignalFilesByCorrelationId(correlationId) {
    const res = await this.getJson(`/api/signal-files/by-correlation/${encodeURIComponent(correlationId)}`);
    const data = await res.json();
    if (!data) return data;

    // Wrap each file in the `Files` array as a SignalFile entity so
    // the UI can call entity helpers (getFormattedCreateTime,
    // getSignalsCount, ...) directly. The top-level payload itself
    // is not a SignalFile — it carries metadata about the calculation
    // (ModelName, ModelId, ModelSignalCombineMode, ...) rather
    // than per-file state — so we return a plain object that mixes
    // the top-level fields with an entity-shaped `Files` array.
    return {
      ...data,
      Files: Array.isArray(data.Files)
        ? data.Files.map((f) => new SignalFile(f))
        : [],
    };
  }

  async downloadSignalFile(id) {
    const path = `/api/signal-files/${id}/download`;
    const url = `${this.baseUrl}${path}`;
    const res = await fetch(url, {
      method: 'GET',
      credentials: 'same-origin',
    });

    if (!res.ok) {
      const txt = await res.text().catch(() => '');
      throw new Error(`Server error: ${res.status} ${txt}`);
    }

    const blob = await res.blob();
    const cd = res.headers.get('content-disposition') || '';
    let filename = `signal_file_${id}.txt`;
    const m = /filename\*=UTF-8''([^;]+)|filename="([^"]+)"|filename=([^;]+)/i.exec(cd);
    if (m) {
      const raw = decodeURIComponent((m[1] || m[2] || m[3] || '').replace(/^['"]|['"]$/g, ''));
      if (raw) filename = raw;
    }

    const objUrl = window.URL.createObjectURL(blob);
    return { url: objUrl, filename };
  }

  // Authentication methods
  async login(email, password, remember = true) {
    const res = await this.postJson('/api/auth/login', {
      Login: email,
      Password: password,
      Remember: remember
    });
    return await res.json();
  }

  async register(email, name, password, passwordConfirm, recaptchaToken) {
    const res = await this.postJson('/api/auth/register', {
      Email: email,
      Name: name,
      Password: password,
      PasswordConfirm: passwordConfirm,
      RecaptchaToken: recaptchaToken
    });
    return await res.json();
  }

  async logout() {
    const res = await this.getJson('/api/auth/logout');
    return res.ok;
  }

  async getCurrentUser() {
    try {
      const res = await this.getJson('/api/auth/me');
      return await res.json();
    } catch (err) {
      // If user is not authenticated, the API returns 401
      if (err.status === 401) {
        return null;
      }
      throw err;
    }
  }

  async updateCurrentUserName(name) {
    const res = await this.putJson('/api/auth/me/name', {
      Name: name
    });
    return await res.json();
  }

  async deleteCurrentUser() {
    const res = await this.deleteJson('/api/auth/me');
    return res.ok;
  }

  async getNotificationPreferences() {
    const res = await this.getJson('/api/notifications/preferences');
    return await res.json();
  }

  async saveNotificationPreferences(preferences) {
    const res = await this.postJson('/api/notifications/preferences', preferences);
    return res.ok;
  }

  async getNotificationTypeTemplates() {
    const res = await this.getJson('/api/notification-types');
    return await res.json();
  }

  async updateNotificationTypeTemplate(notificationType, template) {
    const res = await this.postJson('/api/notification-types', { NotificationType: notificationType, ...template });
    return res.ok;
  }

  async testNotificationType(notificationType) {
    const res = await this.postJson(`/api/notification-types/${encodeURIComponent(notificationType)}/test`, {});
    return res.ok;
  }

  async getInAppNotifications(offset = 0, limit = 25) {
    const res = await this.getJson(`/api/notifications/in-app?offset=${offset}&limit=${limit}`);
    const data = await res.json();
    return {
      Items: (data?.Items || []).map(item => new Notification(item)),
      TotalCount: data?.TotalCount || 0,
    };
  }

  async getInAppUnreadCount() {
    const res = await this.getJson('/api/notifications/in-app/unread-count');
    return await res.json();
  }

  async markInAppNotificationRead(id) {
    const res = await this.postJson(`/api/notifications/in-app/${id}/mark-read`, {});
    return res.ok;
  }

  async markAllInAppNotificationsRead() {
    const res = await this.postJson('/api/notifications/in-app/mark-read', {});
    return res.ok;
  }

  async changePassword(passwordCurrent, passwordNew, passwordNewConfirm) {
    const res = await this.postJson('/api/auth/changepassword', {
      PasswordCurrent: passwordCurrent,
      PasswordNew: passwordNew,
      PasswordNewConfirm: passwordNewConfirm
    });
    return res.ok;
  }

  async getUsersList() {
    const res = await this.getJson('/api/users/list');
    return await res.json();
  }

  async getUsersBriefList() {
    const res = await this.getJson('/api/users/brief-list');
    return await res.json();
  }

  async searchUsers(body) {
    const res = await this.postJson('/api/users/search', body || {});
    return await res.json();
  }

  async searchSources(body) {
    const res = await this.postJson('/api/sources/search', body || {});
    return await res.json();
  }

  async createUser(body) {
    const res = await this.postJson('/api/users', body);
    return await res.json();
  }

  async updateUser(id, body) {
    const res = await this.putJson(`/api/users/${id}`, body);
    return res.ok;
  }

  async generateUserPassword(id) {
    const res = await this.postJson(`/api/users/${id}/generatepassword`, {});
    return await res.json();
  }

  async deleteUser(id) {
    const res = await this.deleteJson(`/api/users/${id}`);
    return res.ok;
  }

  async setUserEmailVerified(id, emailVerified) {
    const res = await this.postJson(`/api/users/${id}/set-email-verified`, {
      EmailVerified: emailVerified
    });
    return res.ok;
  }

  // Ticker management methods
  async createTickersBatch(body) {
    const res = await this.postJson('/api/tickers/create-batch', body);
    return res.ok;
  }

  async pullStaticDetails(body) {
    const res = await this.postJson('/api/tickers/pullstaticdetails', body);
    return res.ok;
  }

  async resetStaticDetails() {
    const res = await this.postJson('/api/tickers/resetstaticdetails', {});
    return await res.json();
  }

  async collectCandles(body) {
    const res = await this.postJson('/api/candles/collect', body);
    return res.ok;
  }

  async getCandleStats() {
    const res = await this.getJson('/api/candles/stats');
    return await res.json();
  }

  async deleteCandles(body) {
    const res = await this.postJson('/api/candles/cleanup', body);
    return await res.json();
  }

  async deleteCandlesBefore(body) {
    const res = await this.postJson('/api/candles/delete-before', body);
    return await res.json();
  }

  async deleteTickers(body) {
    const res = await this.postJson('/api/tickers/cleanup', body);
    return await res.json();
  }

  // Static data methods
  async getTickerTypes() {
    const res = await this.getJson('/api/staticdata/tickertypes');
    const data = await res.json();
    return data.map(item => item.Result);
  }

  async getSources() {
    const res = await this.getJson('/api/staticdata/sources');
    const data = await res.json();
    return data.map(item => item.Result);
  }

  // Statistics methods
  async getStatistics() {
    const res = await this.getJson('/api/statistics');
    return await res.json();
  }

  // Export/Import methods
  async createBackup() {
    const url = `${this.baseUrl}/api/datatransfer/backup/create`;
    const res = await fetch(url, {
      method: 'POST',
      credentials: 'same-origin',
    });

    if (!res.ok) {
      const txt = await res.text().catch(() => '');
      throw new Error(`Server error: ${res.status} ${txt}`);
    }
  }

  async getBackupInfo() {
    const url = `${this.baseUrl}/api/datatransfer/backup/info`;
    const res = await fetch(url, {
      method: 'GET',
      credentials: 'same-origin',
    });

    if (!res.ok) {
      if (res.status === 404) {
        return { exists: false };
      }
      const txt = await res.text().catch(() => '');
      throw new Error(`Server error: ${res.status} ${txt}`);
    }

    return await res.json();
  }

  downloadBackup() {
    // Simply redirect to the download endpoint - browser will handle the download
    const url = `${this.baseUrl}/api/datatransfer/backup/download`;
    window.location.href = url;
  }

  async uploadBackup(file) {
    const url = `${this.baseUrl}/api/datatransfer/backup/upload`;
    const formData = new FormData();
    formData.append('file', file);

    const res = await fetch(url, {
      method: 'POST',
      credentials: 'same-origin',
      body: formData,
    });

    if (!res.ok) {
      const txt = await res.text().catch(() => '');
      throw new Error(`Server error: ${res.status} ${txt}`);
    }

    return await res.json();
  }

  async importData() {
    const url = `${this.baseUrl}/api/datatransfer/import`;

    const res = await fetch(url, {
      method: 'POST',
      credentials: 'same-origin',
    });

    if (!res.ok) {
      const txt = await res.text().catch(() => '');
      throw new Error(`Server error: ${res.status} ${txt}`);
    }
  }

  async deleteBackup() {
    const url = `${this.baseUrl}/api/datatransfer/backup`;

    const res = await fetch(url, {
      method: 'DELETE',
      credentials: 'same-origin',
    });

    if (!res.ok) {
      const txt = await res.text().catch(() => '');
      throw new Error(`Server error: ${res.status} ${txt}`);
    }
  }

  async readPackage(file) {
    const url = `${this.baseUrl}/api/packages/read`;
    const formData = new FormData();
    formData.append('file', file);

    const res = await fetch(url, {
      method: 'POST',
      credentials: 'same-origin',
      body: formData,
    });

    if (!res.ok) {
      const txt = await res.text().catch(() => '');
      throw new Error(`Server error: ${res.status} ${txt}`);
    }

    return await res.json();
  }

  async getOriginalPackageMetadata() {
    const url = `${this.baseUrl}/api/original-data-package/metadata`;
    const res = await fetch(url, {
      method: 'GET',
      credentials: 'same-origin',
    });

    if (!res.ok) {
      const txt = await res.text().catch(() => '');
      throw new Error(`Server error: ${res.status} ${txt}`);
    }

    return await res.json();
  }

  async uploadOriginalPackage(file) {
    const url = `${this.baseUrl}/api/original-data-package/upload`;
    const formData = new FormData();
    formData.append('file', file);

    const res = await fetch(url, {
      method: 'POST',
      credentials: 'same-origin',
      body: formData,
    });

    if (!res.ok) {
      const txt = await res.text().catch(() => '');
      throw new Error(`Server error: ${res.status} ${txt}`);
    }

    return await res.json();
  }

  async deleteOriginalPackage() {
    const url = `${this.baseUrl}/api/original-data-package`;
    const res = await fetch(url, {
      method: 'DELETE',
      credentials: 'same-origin',
    });

    if (!res.ok) {
      // 404 means the folder was already gone — treat as success from
      // the UI's point of view, but surface the message.
      if (res.status === 404) {
        const txt = await res.text().catch(() => '');
        return { message: txt || 'No package folder existed' };
      }
      const txt = await res.text().catch(() => '');
      throw new Error(`Server error: ${res.status} ${txt}`);
    }

    return { message: 'Package removed' };
  }

  async getOriginalCtracPackageMetadata() {
    const url = `${this.baseUrl}/api/original-ctrac-package/metadata`;
    const res = await fetch(url, {
      method: 'GET',
      credentials: 'same-origin',
    });

    if (!res.ok) {
      const txt = await res.text().catch(() => '');
      throw new Error(`Server error: ${res.status} ${txt}`);
    }

    return await res.json();
  }

  async uploadOriginalCtracPackage(file) {
    const url = `${this.baseUrl}/api/original-ctrac-package/upload`;
    const formData = new FormData();
    formData.append('file', file);

    const res = await fetch(url, {
      method: 'POST',
      credentials: 'same-origin',
      body: formData,
    });

    if (!res.ok) {
      const txt = await res.text().catch(() => '');
      throw new Error(`Server error: ${res.status} ${txt}`);
    }

    return await res.json();
  }

  async deleteOriginalCtracPackage() {
    const url = `${this.baseUrl}/api/original-ctrac-package`;
    const res = await fetch(url, {
      method: 'DELETE',
      credentials: 'same-origin',
    });

    if (!res.ok) {
      if (res.status === 404) {
        const txt = await res.text().catch(() => '');
        return { message: txt || 'No CTRAC package folder existed' };
      }
      const txt = await res.text().catch(() => '');
      throw new Error(`Server error: ${res.status} ${txt}`);
    }

    return { message: 'CTRAC package removed' };
  }

  async getDataMigrationCandidates() {
    const url = `${this.baseUrl}/api/data-migration/candidates`;
    const res = await fetch(url, {
      method: 'GET',
      credentials: 'same-origin',
    });

    if (!res.ok) {
      const txt = await res.text().catch(() => '');
      throw new Error(`Server error: ${res.status} ${txt}`);
    }

    return await res.json();
  }

  async migrateOriginalData(request) {
    const url = `${this.baseUrl}/api/data-migration/migrate`;
    const res = await fetch(url, {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(request),
    });

    if (!res.ok) {
      const txt = await res.text().catch(() => '');
      throw new Error(`Server error: ${res.status} ${txt}`);
    }

    return await res.json();
  }

  // System methods
  async getSystemHealth() {
    const url = `${this.baseUrl}/api/system/health`;
    const res = await fetch(url, {
      method: 'GET',
      credentials: 'same-origin',
    });

    if (!res.ok) {
      const txt = await res.text().catch(() => '');
      throw new Error(`Server error: ${res.status} ${txt}`);
    }

    return await res.json();
  }

  async getCtracPackageInfo() {
    const url = `${this.baseUrl}/api/system/ctrac-package-info`;
    const res = await fetch(url, {
      method: 'GET',
      credentials: 'same-origin',
    });

    if (!res.ok) {
      const txt = await res.text().catch(() => '');
      throw new Error(`Server error: ${res.status} ${txt}`);
    }

    return await res.json();
  }

  async cleanupAppDirectory() {
    const url = `${this.baseUrl}/api/system/cleanup-app-directory`;
    const res = await fetch(url, {
      method: 'POST',
      credentials: 'same-origin',
    });

    if (!res.ok) {
      const txt = await res.text().catch(() => '');
      throw new Error(`Server error: ${res.status} ${txt}`);
    }

    return await res.json();
  }

  async getAnalysisPackageInfo() {
    const url = `${this.baseUrl}/api/system/analysis-package-info`;
    const res = await fetch(url, {
      method: 'GET',
      credentials: 'same-origin',
    });

    if (!res.ok) {
      const txt = await res.text().catch(() => '');
      throw new Error(`Server error: ${res.status} ${txt}`);
    }

    return await res.json();
  }

  async getCalculationPackageInfo() {
    const url = `${this.baseUrl}/api/system/calculation-package-info`;
    const res = await fetch(url, {
      method: 'GET',
      credentials: 'same-origin',
    });

    if (!res.ok) {
      const txt = await res.text().catch(() => '');
      throw new Error(`Server error: ${res.status} ${txt}`);
    }

    return await res.json();
  }

  async deleteAnalysisPackage() {
    const url = `${this.baseUrl}/api/system/delete-analysis-package`;
    const res = await fetch(url, {
      method: 'POST',
      credentials: 'same-origin',
    });

    if (!res.ok) {
      const txt = await res.text().catch(() => '');
      throw new Error(`Server error: ${res.status} ${txt}`);
    }

    return await res.json();
  }

  async deleteCalculationPackage() {
    const url = `${this.baseUrl}/api/system/delete-calculation-package`;
    const res = await fetch(url, {
      method: 'POST',
      credentials: 'same-origin',
    });

    if (!res.ok) {
      const txt = await res.text().catch(() => '');
      throw new Error(`Server error: ${res.status} ${txt}`);
    }

    return await res.json();
  }

  // Comparison CSV / sidecar cache — used by the admin "System" page to
  // show file count + on-disk size and to clear the cache when needed.
  async getComparisonStorageInfo() {
    const url = `${this.baseUrl}/api/system/comparison-storage-info`;
    const res = await fetch(url, {
      method: 'GET',
      credentials: 'same-origin',
    });

    if (!res.ok) {
      const txt = await res.text().catch(() => '');
      throw new Error(`Server error: ${res.status} ${txt}`);
    }

    return await res.json();
  }

  async deleteComparisonStorage() {
    const url = `${this.baseUrl}/api/system/delete-comparison-storage`;
    const res = await fetch(url, {
      method: 'POST',
      credentials: 'same-origin',
    });

    if (!res.ok) {
      const txt = await res.text().catch(() => '');
      throw new Error(`Server error: ${res.status} ${txt}`);
    }

    return await res.json();
  }

  async listGeneratedPackages() {
    const res = await this.getJson('/api/storage/packages');
    const data = await res.json();
    return (data || []).map((d) => new GeneratedPackage(d));
  }

  async triggerPackageGeneration() {
    const res = await this.postJson('/api/storage/trigger-generation', {});
    return res.ok;
  }

  async triggerTradingStrategyExecution() {
    const res = await this.postJson('/api/schedulers/trading-strategy-execution', {});
    return res.ok;
  }

  async triggerTradingStrategyAmountAdjustment() {
    const res = await this.postJson('/api/schedulers/trading-strategy-amount-adjustment', {});
    return res.ok;
  }

  async triggerTickersValidation() {
    const res = await this.postJson('/api/schedulers/tickers-validation', {});
    return res.ok;
  }

  async triggerDailyCandleCollection() {
    const res = await this.postJson('/api/schedulers/daily-candle-collection', {});
    return res.ok;
  }

  downloadGeneratedPackage(packageName) {
    const url = `${this.baseUrl}/api/storage/package/${encodeURIComponent(packageName)}`;
    window.location.href = url;
  }

  async calculatePerformance(body) {
    const res = await this.postJson('/api/performance-calculation/calculateImmediately', body);
    return await res.json();
  }

  async calculateBuyAndHold(body) {
    const res = await this.postJson('/api/performance-calculation/buyAndHold', body);
    return await res.json();
  }

  async calculateBuyAndHoldIndex(body) {
    const res = await this.postJson('/api/performance-calculation/buyAndHoldIndex', body);
    return await res.json();
  }

  // ─── Performance Calculation Settings ──────────────────────────────────────
  async searchPerformanceCalculationSettings(body) {
    const res = await this.postJson('/api/performance-calculation/settings/search', body);
    return await res.json();
  }

  async getPerformanceCalculationSettings(id) {
    const res = await this.getJson(`/api/performance-calculation/settings/${id}`);
    return await res.json();
  }

  async savePerformanceCalculationSettings(body) {
    const res = await this.postJson('/api/performance-calculation/settings', body);
    return await res.json();
  }

  async deletePerformanceCalculationSettings(id) {
    await this.deleteJson(`/api/performance-calculation/settings/${id}`);
  }

  async runPerformanceCalculationSettings(id) {
    const res = await this.postJson(`/api/performance-calculation/settings/${id}/run`, {});
    return await res.json();
  }

  async getPerformanceCalculationSettingsResult(id) {
    const res = await this.getJson(`/api/performance-calculation/settings/${id}/result`);
    return await res.json();
  }

  async getPerformanceCalculationAssignments() {
    // Lists every (ModelId, Tag) → settings assignment in the system. The
    // "Show model assignments" modal calls this once on open to render its
    // initial table; subsequent edits/additions re-fetch after each change.
    const res = await this.getJson('/api/performance-calculation/settings/assignments');
    return await res.json();
  }

  async getModelDefaultPerformanceCalculationPortfolioHistory(modelId, tag) {
    // Defaults are now per-(model, tag), so the endpoint requires a tag query
    // param. Callers that don't know which tag they want should pass the
    // model's ActiveSignalTag.
    const params = tag ? `?tag=${encodeURIComponent(tag)}` : '';
    const res = await this.getJson(`/api/models/${modelId}/default-performance-calculation-results/portfolio-history${params}`);
    return await res.json();
  }

  async setModelDefaultPerformanceCalculation(modelId, tag, performanceCalculationId) {
    // Default settings are now keyed by (ModelId, Tag) so the same model
    // can have a different default per signal tag. Both the tag and the
    // settings id are required — pass null/0 for PerformanceCalculationId
    // to clear the default for the given (model, tag).
    await this.putJson(`/api/models/${modelId}/default-performance-calculation`, {
      Tag: tag,
      PerformanceCalculationId: performanceCalculationId,
    });
  }

  async removeModelDefaultPerformanceCalculation(modelId, tag) {
    await this.deleteJson(
      `/api/models/${modelId}/default-performance-calculation?tag=${encodeURIComponent(tag)}`,
    );
  }

  // ─── Trading connectors ──────────────────────────────────────────────
  async getTradingConnectors() {
    const res = await this.getJson('/api/trading-connectors');
    const data = await res.json();
    return (data || []).map((d) => new TradingConnector(d));
  }

  async getTradingConnector(id) {
    const res = await this.getJson(`/api/trading-connectors/${id}`);
    const data = await res.json();
    return new TradingConnector(data);
  }

  async saveTradingConnector(body) {
    const res = await this.postJson('/api/trading-connectors', body);
    const data = await res.json();
    return new TradingConnector(data);
  }

  async deleteTradingConnector(id) {
    const res = await this.deleteJson(`/api/trading-connectors/${id}`);
    return res.ok;
  }

  // ─── Trading strategies ──────────────────────────────────────────────
  async getTradingStrategies({ mineOnly } = {}) {
    // `mineOnly` is an optional override that maps to the
    // ?mineOnly=true|false query string on GET /api/trading-strategies.
    // When omitted the server falls back to its own role-aware default
    // (admin → all, non-admin → own only), so passing nothing preserves
    // the historical behaviour for any caller that hasn't been updated.
    const qs = mineOnly === undefined ? '' : `?mineOnly=${mineOnly ? 'true' : 'false'}`;
    const res = await this.getJson(`/api/trading-strategies${qs}`);
    const data = await res.json();
    return (data || []).map((d) => new TradingStrategy(d));
  }

  async getTradingStrategy(id) {
    const res = await this.getJson(`/api/trading-strategies/${id}`);
    const data = await res.json();
    return new TradingStrategy(data);
  }

  async saveTradingStrategy(body) {
    const res = await this.postJson('/api/trading-strategies', body);
    const data = await res.json();
    return new TradingStrategy(data);
  }

  async deleteTradingStrategy(id) {
    const res = await this.deleteJson(`/api/trading-strategies/${id}`);
    return res.ok;
  }

  async approvePendingTrade(id) {
    const res = await this.postJson(`/api/trading-strategies/${id}/approve-pending-trade`, {});
    return res.ok;
  }

  async declinePendingTrade(id) {
    const res = await this.postJson(`/api/trading-strategies/${id}/decline-pending-trade`, {});
    return res.ok;
  }

  async getTradingStrategyPortfolio(id) {
    const res = await this.getJson(`/api/trading-strategies/${id}/portfolio`);
    return await res.json();
  }

  async getTradingStrategyOrders(id) {
    const res = await this.getJson(`/api/trading-strategies/${id}/orders`);
    return await res.json();
  }

  async getTradingStrategyTradeHistory(id, limit = 50) {
    const res = await this.getJson(
      `/api/trading-strategies/${id}/trade-history?limit=${encodeURIComponent(limit)}`
    );
    return await res.json();
  }

  /**
   * Returns the broker-side portfolio history (equity + PnL per timestamp)
   * for the trading strategy's connector over the requested time window.
   *
   * @param {number} id  — TradingStrategy.Id
   * @param {string} [range='1M']  — one of '1D', '1M', '1Y', 'All'.
   *                                 Matches the buttons on the Portfolio
   *                                 chart's top-right range selector.
   * @returns {Promise<{
   *   TradingConnectorId: number,
   *   TradingConnectorName: string|null,
   *   Currency: string|null,
   *   TimeFrame: string,
   *   BaseValue: number,
   *   Points: Array<{
   *     TimestampUtc: string,
   *     Equity: number,
   *     ProfitLoss: number,
   *     ProfitLossPercentage: number,
   *   }>
   * }>}
   */
  async getTradingStrategyPortfolioHistory(id, range = '1M') {
    const res = await this.getJson(
      `/api/trading-strategies/${id}/portfolio-history?range=${encodeURIComponent(range)}`
    );
    return await res.json();
  }

  /**
   * Returns tickers tradable on the given broker (e.g. Alpaca).
   * Used by the edit page so the money-market / stock pickers only show tickers
   * the broker will actually accept.
   * @param {number} connectorId — TradingConnector.Id, NOT TradingStrategy.Id
   * @param {object} [opts]
   * @param {string} [opts.search] — free-text search (symbol or name substring)
   * @param {number} [opts.limit=50]
   */
  async getTradingConnectorTickers(connectorId, { search = '', limit = 50 } = {}) {
    if (!connectorId) return [];
    const qs = new URLSearchParams();
    if (search) qs.set('search', search);
    if (limit) qs.set('limit', String(limit));
    const tail = qs.toString() ? `?${qs.toString()}` : '';
    const res = await this.getJson(
      `/api/trading-connectors/${connectorId}/tickers${tail}`
    );
    return await res.json();
  }
}


// default instance for convenience
const apiClient = new ApiClient(apiBase/* defaultBase*/);
export default apiClient;
